import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import type { EspecialidadOption } from "./useEspecialidades";
import { COLORS } from "./styles/configuracionOA.styles";
import LogoCardSkeleton from "../../../Loading/LogoCardSkeleton";

interface Props {
    selectedCount: number;

    availableQuery: string;
    selectedQuery: string;
    onAvailableQueryChange: (v: string) => void;
    onSelectedQueryChange: (v: string) => void;

    availableItems: EspecialidadOption[];
    selectedItems: EspecialidadOption[];

    onMoveToSelected: (ids: string[]) => void;
    onMoveToAvailable: (ids: string[]) => void;

    loading: boolean;
    error: string | null;
    disabled?: boolean;
}

export function EspecialidadesTransferList(props: Props) {
    const {
        selectedCount,
        availableQuery,
        selectedQuery,
        onAvailableQueryChange,
        onSelectedQueryChange,
        availableItems,
        selectedItems,
        onMoveToSelected,
        onMoveToAvailable,
        loading,
        error,
        disabled = false,
    } = props;

    const [checkedAvailable, setCheckedAvailable] = useState<Set<string>>(new Set());
    const [checkedSelected, setCheckedSelected] = useState<Set<string>>(new Set());

    const availableIds = useMemo(
        () => availableItems.map((item) => item.id),
        [availableItems]
    );

    const selectedIds = useMemo(
        () => selectedItems.map((item) => item.id),
        [selectedItems]
    );

    const availableCheckedCount = useMemo(
        () => availableIds.filter((id) => checkedAvailable.has(id)).length,
        [availableIds, checkedAvailable]
    );

    const selectedCheckedCount = useMemo(
        () => selectedIds.filter((id) => checkedSelected.has(id)).length,
        [selectedIds, checkedSelected]
    );

    const toggleChecked = (
        side: "available" | "selected",
        id: string
    ) => {
        if (disabled || loading) return;

        if (side === "available") {
            setCheckedAvailable((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
            return;
        }

        setCheckedSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleMoveRight = () => {
        const ids = availableIds.filter((id) => checkedAvailable.has(id));
        if (!ids.length) return;

        onMoveToSelected(ids);

        setCheckedAvailable((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
        });
    };

    const handleMoveLeft = () => {
        const ids = selectedIds.filter((id) => checkedSelected.has(id));
        if (!ids.length) return;

        onMoveToAvailable(ids);

        setCheckedSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
        });
    };

    const renderList = (
        title: string,
        items: EspecialidadOption[],
        checkedSet: Set<string>,
        side: "available" | "selected",
        query: string,
        onQueryChange: (v: string) => void,
        emptyText: string
    ) => (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: "rgba(0,0,0,0.10)",
                backgroundColor: "#fff",
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: COLORS.nestedBg,
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Montserrat",
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: COLORS.section,
                    }}
                >
                    {title} ({items.length})
                </Typography>
            </Box>

            <Box sx={{ px: 2, pt: 1.25 }}>
                <TextField
                    fullWidth
                    variant="standard"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="Buscar especialidad..."
                    disabled={disabled || loading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "rgba(0,0,0,0.45)" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 1,
                        "& .MuiInputBase-input": {
                            fontFamily: "Montserrat",
                            fontSize: "0.95rem",
                            color: "#1D3D4D",
                        },
                    }}
                />
            </Box>

            <List
                dense
                role="list"
                sx={{
                    height: 320,
                    overflow: "auto",
                    px: 1,
                    pb: 1,
                }}
            >
                {items.map((item) => {
                    const labelId = `${side}-transfer-item-${item.id}`;
                    const checked = checkedSet.has(item.id);

                    return (
                        <ListItemButton
                            key={item.id}
                            role="listitem"
                            onClick={() => toggleChecked(side, item.id)}
                            disabled={disabled || loading}
                            sx={{ borderRadius: 1 }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                    checked={checked}
                                    sx={{
                                        color: "#336B87",
                                        "&.Mui-checked": {
                                            color: "#336B87",
                                        },
                                    }}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ "aria-labelledby": labelId }}
                                />
                            </ListItemIcon>

                            <ListItemText
                                id={labelId}
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontFamily: "Montserrat",
                                        fontSize: "0.84rem",
                                        color: "#336B87",
                                        fontWeight: checked ? 700 : 500,
                                    },
                                }}
                            />
                        </ListItemButton>
                    );
                })}

                {!loading && items.length === 0 && (
                    <Typography
                        sx={{
                            px: 2,
                            py: 2,
                            fontFamily: "Montserrat",
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.55)",
                        }}
                    >
                        {emptyText}
                    </Typography>
                )}
            </List>
        </Paper>
    );

    return (
        <Box
            sx={{
                mt: 1,
                ml: { xs: 0, sm: 4 },
                pl: { xs: 0, sm: 2 },
                borderLeft: { xs: "none", sm: `3px solid ${COLORS.nestedBorder}` },
                backgroundColor: { xs: "transparent", sm: COLORS.nestedBg },
                borderRadius: "8px",
                p: { xs: 0, sm: 1.5 },
                opacity: disabled ? 0.7 : 1,
            }}
        >
            <Typography
                sx={{
                    fontFamily: "Montserrat",
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: COLORS.section,
                    letterSpacing: "0.04em",
                    mb: 1,
                }}
            >
                Especialidades seleccionadas: {selectedCount}
            </Typography>

            {loading && (
                <Grid display="flex" justifyContent="center">
                    <LogoCardSkeleton />
                </Grid>
            )}

            {!!error && (
                <Typography
                    sx={{
                        mt: 0.5,
                        mb: 1,
                        fontFamily: "Montserrat",
                        fontSize: "0.8rem",
                        color: "rgba(0,0,0,0.6)",
                    }}
                >
                    {error}
                </Typography>
            )}

            {!loading && !error && (
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        {renderList(
                            "Disponibles",
                            availableItems,
                            checkedAvailable,
                            "available",
                            availableQuery,
                            onAvailableQueryChange,
                            "No hay especialidades disponibles."
                        )}
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "row", md: "column" },
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleMoveRight}
                                disabled={disabled || loading || availableCheckedCount === 0}
                                startIcon={<ChevronRightIcon />}
                                sx={{ minWidth: 120, color: "#336B87" }}
                            >
                                Agregar
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={handleMoveLeft}
                                disabled={disabled || loading || selectedCheckedCount === 0}
                                startIcon={<ChevronLeftIcon />}
                                sx={{ minWidth: 120, color: "#336B87" }}
                            >
                                Quitar
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        {renderList(
                            "Seleccionadas",
                            selectedItems,
                            checkedSelected,
                            "selected",
                            selectedQuery,
                            onSelectedQueryChange,
                            "No hay especialidades seleccionadas."
                        )}
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}